import {
  AlarmIcon,
  EyeIcon,
  EyeSlashIcon,
  GitCommitIcon,
  MoonIcon,
  PlayIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';

import type { AgentSessionActivity, AgentSessionActivityKind } from '@/api-models/agent-sessions';
import { formatTimeAgo } from '@/utils/format';

/**
 * Icon and tone per activity kind.
 *
 * Three tones, not ten: `danger` is reserved for the rows a person has to act on
 * (the session stopped watching, or could not run), `default` is everything that
 * happened as designed. A log where everything is coloured is a log where nothing
 * stands out.
 */
const PRESENTATION: Record<AgentSessionActivityKind, { icon: Icon; tone: 'default' | 'danger' }> = {
  watch_started: { icon: EyeIcon, tone: 'default' },
  watch_stopped: { icon: EyeSlashIcon, tone: 'danger' },
  external_commit: { icon: GitCommitIcon, tone: 'danger' },
  signal_detected: { icon: WarningIcon, tone: 'default' },
  session_woken: { icon: AlarmIcon, tone: 'default' },
  paused: { icon: MoonIcon, tone: 'default' },
  resumed: { icon: PlayIcon, tone: 'default' },
  sandbox_error: { icon: WarningIcon, tone: 'danger' },
  budget_exhausted: { icon: WarningIcon, tone: 'danger' },
  plan_handoff: { icon: PlayIcon, tone: 'default' },
};

const FALLBACK = { icon: WarningIcon, tone: 'default' as const };

function linkFor(metadata: Record<string, unknown> | null): string | null {
  const url = metadata?.['html_url'];
  return typeof url === 'string' && url.startsWith('https://') ? url : null;
}

/**
 * One line in the session's activity log, rendered inline in the thread.
 *
 * Follows the existing centred system-notice shape from `TurnBubble` rather than
 * inventing a second one: these are asides in the conversation, not turns in it.
 * The caller leaves `data-turn-role` unset, which keeps activity out of the
 * quote-to-chat gesture for free.
 */
export function ActivityRow({ activity }: { activity: AgentSessionActivity }) {
  const { icon: ActivityIcon, tone } = PRESENTATION[activity.kind] ?? FALLBACK;
  const href = linkFor(activity.metadata);

  return (
    <div
      className={`flex items-center justify-center gap-1.5 text-xs ${
        tone === 'danger' ? 'text-danger' : 'text-fg-tertiary'
      }`}
    >
      <ActivityIcon size={13} weight="fill" />
      <span>{activity.message}</span>
      {href && (
        <a href={href} target="_blank" rel="noreferrer" className="underline hover:text-fg-secondary">
          View
        </a>
      )}
      <span className="text-fg-muted">· {formatTimeAgo(activity.created_at)}</span>
    </div>
  );
}
